// Authentication and cryptography service using Web Crypto API

/**
 * Generate a random 16-byte salt as a hex string
 */
export function generateSalt(): string {
  const array = new Uint8Array(16)
  window.crypto.getRandomValues(array)
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Generate a random token (e.g., for sessions or password resets)
 */
export function generateToken(length = 32): string {
  const array = new Uint8Array(length)
  window.crypto.getRandomValues(array)
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Hash a password using PBKDF2 (SHA-256)
 * Returns a hex string
 */
export async function hashPassword(
  password: string,
  saltHex: string,
): Promise<string> {
  const enc = new TextEncoder()
  const passwordKey = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits'],
  )

  // Convert hex salt to Uint8Array
  const salt = new Uint8Array(
    saltHex.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16)),
  )

  const hashBuffer = await window.crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    passwordKey,
    256,
  )

  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Verify a password against a hash and salt
 */
export async function verifyPassword(
  password: string,
  hashHex: string,
  saltHex: string,
): Promise<boolean> {
  const computedHash = await hashPassword(password, saltHex)
  return computedHash === hashHex
}

// --- Session Management ---

const SESSION_KEYS = {
  EMAIL: 'configconsole_user_email',
  TOKEN: 'configconsole_session_token',
  CREATED: 'configconsole_session_created',
  LAST_ACTIVE: 'configconsole_last_active',
}

export const SESSION_LIMITS = {
  FULL_LOGIN_DAYS: 14,
  INACTIVITY_MINUTES: 30,
}

export interface SessionState {
  isValid: boolean
  needsPin: boolean
  needsFullLogin: boolean
  email: string | null
}

export const authSession = {
  startSession(email: string, token: string) {
    const now = Date.now().toString()
    localStorage.setItem(SESSION_KEYS.EMAIL, email)
    localStorage.setItem(SESSION_KEYS.TOKEN, token)
    localStorage.setItem(SESSION_KEYS.CREATED, now)
    localStorage.setItem(SESSION_KEYS.LAST_ACTIVE, now)
  },

  updateActivity() {
    if (localStorage.getItem(SESSION_KEYS.TOKEN)) {
      localStorage.setItem(SESSION_KEYS.LAST_ACTIVE, Date.now().toString())
    }
  },

  clearSession() {
    localStorage.removeItem(SESSION_KEYS.EMAIL)
    localStorage.removeItem(SESSION_KEYS.TOKEN)
    localStorage.removeItem(SESSION_KEYS.CREATED)
    localStorage.removeItem(SESSION_KEYS.LAST_ACTIVE)
  },

  getSessionState(): SessionState {
    const email = localStorage.getItem(SESSION_KEYS.EMAIL)
    const token = localStorage.getItem(SESSION_KEYS.TOKEN)
    const createdStr = localStorage.getItem(SESSION_KEYS.CREATED)
    const lastActiveStr = localStorage.getItem(SESSION_KEYS.LAST_ACTIVE)

    if (!email || !token || !createdStr || !lastActiveStr) {
      return {
        isValid: false,
        needsPin: false,
        needsFullLogin: true,
        email: null,
      }
    }

    const now = Date.now()
    const created = parseInt(createdStr, 10)
    const lastActive = parseInt(lastActiveStr, 10)

    // Check if 14 days have passed since full login
    const daysSinceLogin = (now - created) / (1000 * 60 * 60 * 24)
    if (daysSinceLogin >= SESSION_LIMITS.FULL_LOGIN_DAYS) {
      return { isValid: false, needsPin: false, needsFullLogin: true, email }
    }

    // Check if 30 minutes of inactivity have passed
    const minutesSinceActive = (now - lastActive) / (1000 * 60)
    if (minutesSinceActive >= SESSION_LIMITS.INACTIVITY_MINUTES) {
      return { isValid: true, needsPin: true, needsFullLogin: false, email }
    }

    return { isValid: true, needsPin: false, needsFullLogin: false, email }
  },

  getEmail(): string | null {
    return localStorage.getItem(SESSION_KEYS.EMAIL)
  },

  getToken(): string | null {
    return localStorage.getItem(SESSION_KEYS.TOKEN)
  },
}
